import { forwardRef, useEffect, useRef, useState } from "react";
import { Camera } from "@phosphor-icons/react";

const W = 1000;
const H = 700;

const COPY = {
  en: {
    title: "Certificate of Completion",
    certifies: "This certifies that",
    body: "has successfully passed the DSLR Advisor Final Course Exam, covering manual camera settings, light, exposure, composition, gear and the history of photography.",
    score: "Score",
    date: "Date",
    id: "Certificate ID",
    issuer: "Issued by",
    verify: "Verify at",
  },
  es: {
    title: "Certificado de Finalización",
    certifies: "Se certifica que",
    body: "ha aprobado con éxito el Examen Final del Curso de DSLR Advisor, que abarca ajustes manuales de cámara, luz, exposición, composición, equipo e historia de la fotografía.",
    score: "Puntaje",
    date: "Fecha",
    id: "ID del certificado",
    issuer: "Emitido por",
    verify: "Verificar en",
  },
};

export const certificateUrl = (id) => `${window.location.origin}/certificate/${id}`;

export const CertificateCard = forwardRef(function CertificateCard({ cert, lang }, ref) {
  const c = COPY[lang] || COPY.en;
  const pct = Math.round((cert.score / cert.total) * 100);
  const date = new Date(cert.issued_at).toLocaleDateString(lang === "es" ? "es" : "en", { year: "numeric", month: "long", day: "numeric" });
  const host = window.location.host;
  return (
    <div ref={ref} style={{ width: W, height: H }} className="relative bg-[#0A0A0A] text-white overflow-hidden font-['Outfit']" data-testid="certificate-card">
      <div className="absolute inset-5 border-2 border-[#F5A524]/80 rounded-md" />
      <div className="absolute inset-8 border border-[#F5A524]/25 rounded-sm" />
      <div className="relative h-full flex flex-col items-center text-center px-24 pt-14 pb-12">
        <div className="flex-1 flex flex-col items-center justify-center">
        <div className="flex items-center gap-2.5">
          <Camera weight="fill" className="w-9 h-9 text-[#F5A524]" />
          <span className="text-2xl font-semibold tracking-tight">DSLR Advisor</span>
        </div>
        <p className="mt-8 text-[#F5A524] uppercase tracking-[0.35em] text-sm font-semibold">{c.title}</p>
        <p className="mt-8 text-[#A1A1AA] text-lg">{c.certifies}</p>
        <p className="mt-2 text-6xl font-semibold tracking-tight leading-tight" data-testid="certificate-name">{cert.name}</p>
        <div className="mt-4 w-48 h-px bg-[#F5A524]/60" />
        <p className="mt-6 text-[#D4D4D8] text-lg leading-relaxed max-w-[720px]">{c.body}</p>
        </div>
        <div className="mt-6 w-full grid grid-cols-3 items-end text-left">
          <div>
            <p className="text-[#71717A] text-xs uppercase tracking-[0.2em]">{c.score}</p>
            <p className="font-['JetBrains_Mono'] text-xl leading-8 text-[#F5A524]" data-testid="certificate-score">{cert.score}/{cert.total} · {pct}%</p>
          </div>
          <div className="text-center">
            <p className="text-[#71717A] text-xs uppercase tracking-[0.2em]">{c.date}</p>
            <p className="text-xl leading-8">{date}</p>
          </div>
          <div className="text-right">
            <p className="text-[#71717A] text-xs uppercase tracking-[0.2em]">{c.issuer}</p>
            <p className="text-xl leading-8 font-semibold">DSLR Advisor</p>
          </div>
        </div>
        <p className="mt-6 text-[#52525B] text-xs font-['JetBrains_Mono']">
          {c.id}: {cert.cert_id} · {c.verify} {host}/certificate/{cert.cert_id}
        </p>
      </div>
    </div>
  );
});

export const SQUARE = 1080;

const POST = {
  en: { passed: "Passed the Final Course Exam", score: "Score", tag: "Certified in manual DSLR settings" },
  es: { passed: "Aprobó el Examen Final del Curso", score: "Puntaje", tag: "Certificado en ajustes manuales DSLR" },
};

export const CertificateSquare = forwardRef(function CertificateSquare({ cert, lang }, ref) {
  const c = COPY[lang] || COPY.en;
  const p = POST[lang] || POST.en;
  const pct = Math.round((cert.score / cert.total) * 100);
  const date = new Date(cert.issued_at).toLocaleDateString(lang === "es" ? "es" : "en", { year: "numeric", month: "long", day: "numeric" });
  return (
    <div ref={ref} style={{ width: SQUARE, height: SQUARE }} className="relative bg-[#0A0A0A] text-white overflow-hidden font-['Outfit']" data-testid="certificate-square">
      <div className="absolute inset-8 border-[3px] border-[#F5A524]/80 rounded-xl" />
      <div className="absolute inset-12 border border-[#F5A524]/25 rounded-lg" />
      <div className="relative h-full flex flex-col items-center justify-center text-center px-28">
        <div className="flex items-center gap-3 leading-none">
          <Camera weight="fill" className="w-14 h-14 text-[#F5A524]" />
          <span className="text-4xl font-semibold tracking-tight leading-none">DSLR Advisor</span>
        </div>
        <p className="mt-14 text-[#F5A524] uppercase tracking-[0.35em] text-xl font-semibold">{c.title}</p>
        <p className="mt-12 font-semibold tracking-tight leading-tight" style={{ fontSize: cert.name.length > 26 ? 56 : cert.name.length > 16 ? 68 : 84 }}>{cert.name}</p>
        <div className="mt-10 w-64 h-[2px] bg-[#F5A524]/60" />
        <p className="mt-10 text-[#D4D4D8] text-3xl">{p.passed}</p>
        <div className="mt-14 flex items-center gap-4 bg-[#F5A524]/10 border border-[#F5A524]/40 rounded-2xl px-10 h-24">
          <span className="text-[#A1A1AA] uppercase tracking-[0.2em] text-lg leading-none">{p.score}</span>
          <span className="font-['JetBrains_Mono'] text-5xl text-[#F5A524] leading-none">{cert.score}/{cert.total}</span>
          <span className="font-['JetBrains_Mono'] text-3xl text-white leading-none">{pct}%</span>
        </div>
        <p className="mt-12 text-[#A1A1AA] text-2xl">{date}</p>
        <p className="mt-3 text-[#71717A] text-xl">{p.tag} · #DSLRAdvisor</p>
      </div>
    </div>
  );
});

export function ScaledCertificate({ cert, lang }) {
  const wrap = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setScale(Math.min(1, e.contentRect.width / W)));
    ro.observe(wrap.current);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={wrap} className="w-full" style={{ height: H * scale }}>
      <div style={{ width: W, transform: `scale(${scale})`, transformOrigin: "top left" }} className="rounded-lg overflow-hidden shadow-2xl shadow-black/50">
        <CertificateCard cert={cert} lang={lang} />
      </div>
    </div>
  );
}
